// src/client/screens/borrower/RepayScreen.tsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Smartphone } from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { useActiveLoan } from '@client/hooks/useLoan';
import { useInitiateRepayment } from '@client/hooks/useRepayment';
import { subscribeToPaymentStatus } from '@client/services/paymentService';
import {
  PROVIDER_DISPLAY_NAME, SUPPORTED_MM_PROVIDERS, type MobileMoneyProvider,
} from '@/constants/config';
import type { PaymentFixture } from '@client/lib/fixtures';
import NavBar from '@client/components/NavBar';
import Pill from '@client/components/Pill';
import SectionLabel from '@client/components/SectionLabel';
import Button from '@client/components/Button';
import { useToast } from '@client/components/ToastProvider';
import { useAuth } from '@client/auth/useAuth';
import { ngweeToKwachaNumber } from '@client/utils/currency';
import { kwachaToNgwee, type Ngwee } from '@/lib/types';
import { formatDueIn, daysUntil } from '@client/utils/dates';

function maskPhone(phone: string): string {
  // +260977123456 → +260 977 ••• 456
  return phone.replace(/(\+260)(\d{3})(\d{3})(\d{3})/, '$1 $2 ••• $4');
}

const STATUS_LABEL: Record<PaymentFixture['status'], string> = {
  initiated: 'Initiated…',
  pending:   'Awaiting your USSD confirmation…',
  completed: 'Payment confirmed',
  failed:    'Payment failed',
  timeout:   'Timed out — please try again',
  reconciled: 'Reconciled',
};

export default function RepayScreen() {
  const { data: loan } = useActiveLoan();
  const { profile } = useAuth();
  const repay = useInitiateRepayment();
  const [provider, setProvider] = useState<MobileMoneyProvider>('mtn_momo');
  const [payFull, setPayFull] = useState(true);
  const [pendingPayment, setPendingPayment] = useState<{ id: string; status: PaymentFixture['status'] } | null>(null);
  const navigate = useNavigate();
  const toast = useToast();

  // Real-time payment status subscription
  useEffect(() => {
    if (!pendingPayment) return;
    const unsub = subscribeToPaymentStatus(pendingPayment.id, (status) => {
      setPendingPayment((prev) => prev ? { ...prev, status } : null);
    });
    return unsub;
  }, [pendingPayment?.id]);

  // Auto-navigate home 1.5 s after completion
  useEffect(() => {
    if (pendingPayment?.status !== 'completed') return;
    const t = setTimeout(() => navigate(ROUTES.home, { replace: true }), 1500);
    return () => clearTimeout(t);
  }, [pendingPayment?.status, navigate]);

  // Empty state — no active loan
  if (!loan) {
    return (
      <div className="app-shell app-shell--with-nav">
        <main className="scroll-area" style={{ padding: '20px 24px 24px' }}>
          <h1 style={{
            fontFamily: 'var(--ff-display)',
            fontSize: 44,
            color: 'var(--c-text)',
            letterSpacing: -1,
            lineHeight: 1,
            margin: '0 0 20px',
            fontWeight: 400,
          }}>
            Repay
          </h1>
          <div style={{
            background: 'var(--c-surface)',
            border: '1px dashed var(--c-border)',
            borderRadius: 18,
            padding: '40px 24px',
            textAlign: 'center',
          }}>
            <p style={{ margin: 0, color: 'var(--c-text-muted)', fontSize: 15 }}>
              Nothing to repay right now.
            </p>
          </div>
        </main>
        <NavBar />
      </div>
    );
  }

  const outstandingKwacha = ngweeToKwachaNumber(loan.outstanding_ngwee);
  const totalKwacha = ngweeToKwachaNumber(loan.total_due_ngwee);
  const paidKwacha = Math.max(0, totalKwacha - outstandingKwacha);
  const paidPct = totalKwacha > 0 ? Math.round((paidKwacha / totalKwacha) * 100) : 0;
  const minPayKwacha = Math.max(50, Math.ceil(outstandingKwacha / 2 / 50) * 50);
  const minPayNgwee = kwachaToNgwee(minPayKwacha) as Ngwee;

  const due = loan.due_at;
  const daysLeft = due ? daysUntil(due) : null;

  const disbursedAt = loan.disbursed_at ? new Date(loan.disbursed_at) : null;
  const daysElapsed = disbursedAt
    ? Math.max(0, Math.floor((Date.now() - disbursedAt.getTime()) / 86_400_000))
    : 0;
  const daysRemaining = Math.max(0, loan.term_days - daysElapsed);
  const maskedPhone = profile?.phone ? maskPhone(profile.phone) : null;

  const submit = () => {
    const amount = payFull ? loan.outstanding_ngwee : minPayNgwee;
    repay.mutate(
      { loanId: loan.id, amount, provider },
      {
        onSuccess: (payment) => {
          setPendingPayment({ id: payment.id, status: payment.status });
          toast.show('Check your phone for the USSD prompt', 'success');
        },
        onError: () => toast.show('Could not initiate payment', 'error'),
      },
    );
  };

  return (
    <div className="app-shell app-shell--with-nav">
      <main className="scroll-area" style={{ padding: 0 }}>
        {/* Title */}
        <div style={{ padding: '20px 24px 4px' }}>
          <h1 style={{
            fontFamily: 'var(--ff-display)',
            fontSize: 44,
            color: 'var(--c-text)',
            letterSpacing: -1,
            lineHeight: 1,
            margin: 0,
            fontWeight: 400,
          }}>
            Repay
          </h1>
          <div style={{
            marginTop: 6,
            fontSize: 13,
            color: 'var(--c-text-muted)',
            fontFamily: 'var(--ff-mono)',
          }}>
            {loan.loan_number}{due ? ` · due ${new Date(due).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}
          </div>
        </div>

        {/* Outstanding card */}
        <div style={{ padding: '20px 24px 0' }}>
          <div style={{
            background: 'var(--c-surface)',
            border: '1px solid var(--c-border)',
            borderRadius: 18,
            padding: 20,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div className="eyebrow">Outstanding</div>
                <div style={{
                  fontFamily: 'var(--ff-display)',
                  fontSize: 44,
                  color: 'var(--c-text)',
                  letterSpacing: -1.2,
                  marginTop: 4,
                  lineHeight: 1,
                }}>
                  K{outstandingKwacha.toLocaleString()}
                  <span style={{ fontSize: 22, color: 'var(--c-text-muted)' }}>.00</span>
                </div>
              </div>
              {due && daysLeft !== null && (
                <Pill tone={daysLeft < 0 ? 'danger' : 'warning'}>
                  {formatDueIn(due)}
                </Pill>
              )}
            </div>
            <div style={{
              marginTop: 18, height: 6,
              background: 'var(--c-chart-track)',
              borderRadius: 3, overflow: 'hidden',
            }}>
              <div style={{
                width: `${paidPct}%`, height: '100%',
                background: 'var(--c-accent)', borderRadius: 3,
              }} />
            </div>
            <div style={{
              marginTop: 8,
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 11,
              color: 'var(--c-text-muted)',
              fontFamily: 'var(--ff-mono)',
            }}>
              <span>K{paidKwacha.toLocaleString()} paid</span>
              <span>{paidPct}%</span>
            </div>
            <div style={{
              marginTop: 12,
              paddingTop: 12,
              borderTop: '1px solid var(--c-border-lt)',
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 12,
              color: 'var(--c-text-muted)',
            }}>
              <span>{daysRemaining} days remaining</span>
              <span>{loan.term_days}-day loan</span>
            </div>
          </div>
        </div>

        {pendingPayment ? (
          // Status display while a payment is in flight
          <div style={{ padding: '20px 24px 0' }}>
            <div style={{
              background: 'var(--c-surface)',
              border: '1px solid var(--c-border)',
              borderRadius: 18,
              padding: 24,
              textAlign: 'center',
            }}>
              <p style={{ margin: 0, fontSize: 16, color: 'var(--c-text)', fontWeight: 500 }}>
                {STATUS_LABEL[pendingPayment.status]}
              </p>
              {(pendingPayment.status === 'failed' || pendingPayment.status === 'timeout') && (
                <div style={{ marginTop: 16 }}>
                  <Button block size="lg" onClick={() => setPendingPayment(null)}>
                    Try again
                  </Button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Pay with */}
            <div style={{ padding: '0 24px' }}>
              <SectionLabel>Pay with</SectionLabel>
            </div>
            <div style={{ padding: '0 24px' }}>
              {SUPPORTED_MM_PROVIDERS.map((p) => {
                const selected = provider === p;
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setProvider(p)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      padding: '14px 16px',
                      marginBottom: 8,
                      background: selected ? 'var(--c-accent-soft)' : 'var(--c-surface)',
                      border: `1px solid ${selected ? 'var(--c-accent)' : 'var(--c-border)'}`,
                      borderRadius: 14,
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{
                      width: 38, height: 38, borderRadius: 10,
                      background: 'var(--c-surface-alt)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Smartphone size={18} color="var(--c-text)" />
                    </span>
                    <span style={{ flex: 1, textAlign: 'left' }}>
                      <span style={{
                        display: 'block',
                        fontSize: 14,
                        color: 'var(--c-text)',
                        fontWeight: 500,
                      }}>
                        {PROVIDER_DISPLAY_NAME[p]}
                      </span>
                      {maskedPhone && (
                        <span style={{
                          display: 'block',
                          fontSize: 11,
                          color: 'var(--c-text-muted)',
                          fontFamily: 'var(--ff-mono)',
                          marginTop: 2,
                        }}>
                          {maskedPhone}
                        </span>
                      )}
                    </span>
                    <span style={{
                      width: 18, height: 18, borderRadius: 9,
                      border: `1.5px solid ${selected ? 'var(--c-accent)' : 'var(--c-border)'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {selected && (
                        <span style={{
                          width: 8, height: 8, borderRadius: 4,
                          background: 'var(--c-accent)',
                        }} />
                      )}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Amount */}
            <div style={{ padding: '0 24px' }}>
              <SectionLabel>Amount</SectionLabel>
            </div>
            <div style={{
              padding: '0 24px',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}>
              {[
                { id: 'min',  label: 'Minimum',      value: minPayKwacha, full: false },
                { id: 'full', label: 'Full balance', value: outstandingKwacha, full: true },
              ].map((opt) => {
                const selected = payFull === opt.full;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setPayFull(opt.full)}
                    style={{
                      padding: 16,
                      borderRadius: 14,
                      background: selected ? 'var(--c-accent-soft)' : 'var(--c-surface)',
                      border: `1px solid ${selected ? 'var(--c-accent)' : 'var(--c-border)'}`,
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <div className="eyebrow" style={{ letterSpacing: 1 }}>{opt.label}</div>
                    <div style={{
                      fontFamily: 'var(--ff-display)',
                      fontSize: 28,
                      color: 'var(--c-text)',
                      letterSpacing: -0.6,
                      marginTop: 4,
                      lineHeight: 1,
                    }}>
                      K{opt.value.toLocaleString()}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Pay button */}
            <div style={{ padding: '24px 24px 0' }}>
              <Button
                block size="lg" loading={repay.isPending}
                onClick={submit}
                rightIcon={<ArrowRight size={18} />}
              >
                Pay K{(payFull ? outstandingKwacha : minPayKwacha).toLocaleString()}.00
              </Button>
            </div>
          </>
        )}

        <div style={{ height: 24 }} />
      </main>

      <NavBar />
    </div>
  );
}
